import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	getImportRecordForTransferToLocationEquipment,
	TRANSFER_TO_LOCATION_EQUIPMENT_IMPORT_HEADER,
} from './stock_location_constants';
import { createAssetFromEquipmentPerStockLocationBulkForSupplier } from '../../thunks/assets_thunks';
import { assetTypesRestCrudThunksForSupplier } from '../../thunks/asset_types_thunks';
import { locationsRestCrudThunksForSupplier } from '../../thunks/locations_thunks';
import { assetModelsRestCrudThunksForSupplier } from '../../thunks/asset_models_thunks';
import OWImporter from '../ow_import_grid/OWImporter';
import { REGEX_CONFIG_NAMES, regexRecordHook } from '../ow_import_grid/import_record_hooks';

const TransferToLocationReceivedEquipmentBulk: FC<any> = ({
	selectedRecords,
	onSuccess,
	fetchAssetTypes,
	fetchLocations,
	fetchAssetModels,
	companyConfig,
	createAssetFromEquipmentBulk,
	onClose,
	transferInitiated,
}): React.ReactElement => {
	const [assetTypes, setAssetTypes] = useState([]);
	const [locations, setLocations] = useState([]);
	const [assetModels, setAssetModels] = useState([]);
	const [canShowImporter, setCanShowImporter] = useState(false);

	const transferNoteRequired = useMemo(
		() => nullSafeGetOrElse('inventoryConfig.transferNoteMandatory', companyConfig, false),
		[companyConfig]
	);

	useEffect(() => {
		const promises = [fetchAssetTypes(), fetchLocations(), fetchAssetModels()];
		Promise.allSettled(promises)
			.then((res) => {
				const [{ value: assetTypes = [] }, { value: locations = [] }, { value: assetModels = [] }] =
					res;
				setAssetTypes(assetTypes);
				setLocations(locations);
				setAssetModels(assetModels);
			})
			.finally(() => {
				transferInitiated && transferInitiated();
			});
	}, []);

	useEffect(() => {
		locations && locations.length > 0 && setCanShowImporter(true);
	}, [locations]);

	const getPossibleAssetModelId = useCallback(
		(id, assetTypeId) => {
			const equipmentTypeModelName = nullSafeGet(
				'equipmentType.modelName',
				selectedRecords.find((_) => _.id === id)
			);
			const matchingAssetModel =
				assetTypeId && equipmentTypeModelName
					? (assetModels || []).find(
							(_) =>
								_.assetTypeId === assetTypeId &&
								nullSafeGetOrElse('modelName', _, '').includes(equipmentTypeModelName.trim())
					  )
					: undefined;
			return nullSafeGetOrElse('id', matchingAssetModel, undefined);
		},
		[assetModels, selectedRecords]
	);

	const addPossibleAssetModelId = useCallback(
		(record) => {
			const assetTypeId = record['assetTypeId'];
			const id = record.id;

			return { ...record, assetModelId: getPossibleAssetModelId(id, assetTypeId) };
		},
		[getPossibleAssetModelId]
	);

	const recordHook = useCallback(
		(record) => {
			const assetTypeId = record.get('assetTypeId');
			const id = record.get('id');

			record.set('assetModelId', getPossibleAssetModelId(id, assetTypeId));

			return regexRecordHook([REGEX_CONFIG_NAMES.TRANSFER_DATE])(record);
		},
		[getPossibleAssetModelId]
	);

	const onDataUpload = useCallback(
		({ validData }) => {
			return new Promise((resolve, reject) => {
				const entities = validData.map(
					({ id, locationId, assetModelId, assetTypeId, assetNumber, transferNOte }) => ({
						equipmentPerStockLocationId: id,
						locationId,
						...(assetModelId && { assetModelId }),
						...(assetTypeId && { assetTypeId }),
						...(assetNumber && { assetNumber }),
						...(transferNOte && { transferNOte }),
					})
				);
				createAssetFromEquipmentBulk(entities)
					.then((res) => {
						resolve('Transferred successfully!');
						onSuccess && onSuccess(res);
					})
					.catch(reject);
			});
		},
		[createAssetFromEquipmentBulk, onSuccess]
	);

	const _onTransferComplete = useCallback(() => {
		setCanShowImporter(false);
		onClose && onClose();
	}, [onClose]);

	return canShowImporter ? (
		<OWImporter
			dataLoadComplete={canShowImporter}
			type="equipment_level"
			fields={TRANSFER_TO_LOCATION_EQUIPMENT_IMPORT_HEADER(
				locations.map((_) => ({
					label: _.name,
					value: _.id,
				})),
				assetTypes.map((_) => ({
					label: _.name,
					value: _.id,
				})),
				assetModels.map((_) => ({
					label: _.modelName,
					value: _.id,
				})),
				transferNoteRequired
			)}
			recordHook={recordHook}
			data={selectedRecords.map((_) =>
				addPossibleAssetModelId(getImportRecordForTransferToLocationEquipment(_))
			)}
			onComplete={_onTransferComplete}
			onDataLoad={onDataUpload}
		/>
	) : null;
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	fetchAssetTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			assetTypesRestCrudThunksForSupplier.readLite(
				{ ...params, no_pagination: true },
				'bulkEditAssetsAssetTypes',
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),

	fetchLocations: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			locationsRestCrudThunksForSupplier.readLite(
				{ ...params, no_pagination: true },
				'bulkEditAssetsLocations',
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchAssetModels: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			assetModelsRestCrudThunksForSupplier.readLite(
				{ ...params, no_pagination: true },
				'bulkEditAssetsAssetModels',
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	createAssetFromEquipmentBulk: (entities) =>
		dispatch(createAssetFromEquipmentPerStockLocationBulkForSupplier(entities)),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(TransferToLocationReceivedEquipmentBulk)
);
